'use client';

import { PortalModal, usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import { FC, SyntheticEvent } from 'react';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import ActualIcon from '@/public/icons/new_releases.svg';
import { Header } from '@/src/components/Header';
import { PackPreviewSeeAll } from '@/src/components/PackPreviewSeeAll';
import {
  useProfileContext,
  useStyleContext,
  useMobileContext,
} from '@/src/providers';
import { getImageByGender } from '@/src/utils/getImageByGender';

const styles = {
  portal:
    '!h-dvh !max-h-none !min-h-dvh !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'flex gap-2 items-center mb-4',
  headWrap:
    'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center bg-fill-menu dark:bg-fill-menu bg-fill-menuBg',
  icon: 'w-5 h-5 fill-current',
  iconStyle: 'w-4 h-4 fill-current',
  title: 'text-base font-bold',
  name: 'text-base font-bold mb-3',
  wrapper:
    'grid grid-cols-2 row-auto gap-x-4 gap-y-3 justify-between bg-fill-menu pb-5',
  innerWrap: 'shrink-0 bg-fill-menu px-5 pt-16',
  styleItem: 'shrink-0 relative overflow-hidden rounded-lg',
  preview: 'w-full h-[180px] object-cover object-top',
  tag: 'absolute top-1 right-1',
  gradient: 'absolute top-0 left-0 right-0 bottom-0 bg-gradient-sm',
  info: 'absolute left-3 bottom-3',
  styleName: 'mb-[2px] text-sm font-medium text-left',
  styleCount: 'text-sm font-medium text-label-secondary text-left',
  closeButton: 'absolute top-7 z-2',
  closeIcon: 'w-6 h-6',
};

export const SeeAllTrendingPortal: FC = () => {
  const profile = useProfileContext();
  const {
    trendingItems,
    setPrevPortal,
    genderMode,
    changeActiveCategory,
    setPortalSeeAllMode,
    changeActiveStyle,
  } = useStyleContext();
  const { openPortal, closePortal } = usePortalContext();
  const t = useTranslations('MenuPage');
  const { isMobiles } = useMobileContext();

  const handleClickImage = (event: SyntheticEvent) => {
    const styleId = event.currentTarget.getAttribute('data-style-id');

    const currentTrending = trendingItems?.find((item) => item.id === styleId);

    if (currentTrending?.type! === 'StyleCategory') {
      if (currentTrending?.relationId) {
        changeActiveCategory(currentTrending?.relationId, 'style');
      }
    }

    const portalType =
      currentTrending?.type === 'StyleCategory'
        ? 'styles'
        : currentTrending?.type === 'PhotoSession'
          ? 'photoSessions'
          : currentTrending?.type === 'CombiVideo'
            ? 'combiVideos'
            : currentTrending?.type === 'VideoLook'
              ? 'videoLooks'
              : currentTrending?.type === 'Effect'
                ? 'effects'
                : 'videoStyles';

    setPrevPortal('trending');
    setPortalSeeAllMode(portalType);
    closePortal();

    if (currentTrending?.relationId) {
      changeActiveStyle(currentTrending?.relationId, portalType);
    }

    if (currentTrending?.type === 'StyleCategory') {
      openPortal(PORTALS.SeeAllImages);
    } else {
      openPortal(PORTALS.GenerateImage);
    }
  };

  if (!profile) {
    return null;
  }

  return (
    <PortalModal
      portalId={PORTALS.SeeAllTrending}
      className={styles.portal}
      noClose
    >
      <div className="h-full relative">
        <div className={twMerge(styles.headWrap, isMobiles && 'pt-[100px]')}>
          <Header isCloseButton onClickCloseButton={closePortal} />
        </div>
        <div
          className={twMerge(
            'h-full overflow-y-auto',
            isMobiles && 'pt-[100px]',
          )}
        >
          <div className={styles.innerWrap}>
            <div className={styles.head}>
              <ActualIcon className={styles.icon} />
              <div className={styles.title}>{t('actualTitle')}</div>
            </div>

            <div className={styles.wrapper}>
              {trendingItems?.map((item, i) => (
                <PackPreviewSeeAll
                  key={item.id}
                  id={item.id}
                  index={i}
                  isPopular={false}
                  isNew={false}
                  name={item.name[profile?.lang]}
                  image={getImageByGender(item, genderMode)!}
                  handleClickImage={handleClickImage}
                  isVideo
                  isSmall
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
